<template>
  <BittsQuickFilters
    class="partner-tab-actions"
    :filter-options="filterOptions"
    :filter-value-options="filterValueOptions"
    @update-filters="handleUpdateFilters"
  >
    <template #prefix-controls>
      <slot name="segment" />
    </template>
    <!-- Renders nothing for the other filters, which then fall back to their
         own option lists. -->
    <template #content="{ filter }">
      <PartnerTabPopulationFilter
        v-if="filter.value === PARTNER_POPULATIONS_FILTER"
        side="partner"
      />
      <PartnerTabPopulationFilter
        v-else-if="filter.value === MY_POPULATIONS_FILTER"
        side="mine"
      />
    </template>
    <template #controls>
      <div class="flex items-center gap-6">
        <BittsInput
          v-model="searchValue"
          type="search"
          name="partner-search"
          placeholder="Search partners"
          size="small"
          @update:model-value="debouncedSearch"
        />
        <slot name="actions" />
      </div>
    </template>
  </BittsQuickFilters>
</template>

<script setup lang="ts">
import { BittsInput, BittsQuickFilters } from '@crossbeam/bitts';
import type { FilterProp } from '@crossbeam/bitts/src/BittsQuickFilter/types';

import { useDebounceFn } from '@vueuse/core';
import { computed, ref } from 'vue';

import {
  OWNER_FILTER,
  PARTNER_FILTER,
  type PartnerTabFilterValues,
  TAGS_FILTER,
} from '../composables/usePartnerTabFilters';

import PartnerTabPopulationFilter from './PartnerTabPopulationFilter.vue';

const { partnerOptions, ownerOptions, tagOptions } = defineProps<{
  partnerOptions: FilterProp[];
  ownerOptions: FilterProp[];
  tagOptions: FilterProp[];
}>();
const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: PartnerTabFilterValues];
}>();
const PARTNER_POPULATIONS_FILTER = 'partner_populations';
const MY_POPULATIONS_FILTER = 'my_populations';

const filterOptions: FilterProp[] = [
  {
    label: 'Partner Populations',
    value: PARTNER_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'My Populations',
    value: MY_POPULATIONS_FILTER,
    faIcon: ['fak', 'population'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Partner',
    value: PARTNER_FILTER,
    faIcon: ['fak', 'partner'],
    datatype: 'text',
    useSearch: true,
    noCustomValue: true,
  },
  {
    label: 'Owner',
    value: OWNER_FILTER,
    faIcon: ['far', 'user'],
    datatype: 'text',
    noCustomValue: true,
  },
  {
    label: 'Tags',
    value: TAGS_FILTER,
    faIcon: ['far', 'tag'],
    datatype: 'text',
    noCustomValue: true,
  },
];

const filterValueOptions = computed<Record<string, FilterProp[]>>(() => ({
  [PARTNER_FILTER]: partnerOptions,
  [OWNER_FILTER]: ownerOptions,
  [TAGS_FILTER]: tagOptions,
}));

const searchValue = ref('');

const debouncedSearch = useDebounceFn((value: string) => {
  emit('search-applied', value);
}, 300);

/**
 * BittsQuickFilters hands back every datatype it supports; the simple filters
 * here only ever carry arrays, so anything else is treated as unset.
 */
function handleUpdateFilters({
  filterValues,
}: {
  filterValues: Record<
    string,
    (string | number)[] | Record<string, unknown> | null
  >;
}) {
  const applied: PartnerTabFilterValues = {};
  Object.entries(filterValues).forEach(([key, value]) => {
    if (Array.isArray(value)) applied[key] = value;
  });
  emit('filters-applied', applied);
}
</script>
